import React, { useState, useEffect, useRef } from 'react';
import { Bot, Mic, Server, Zap, Activity, GitBranch, Globe, Github, ArrowRight } from 'lucide-react';
import { portfolioData } from '../data/mock';

const focusIcons = {
  bot: Bot,
  mic: Mic,
  server: Server,
  zap: Zap,
  activity: Activity,
  'git-branch': GitBranch,
  globe: Globe,
  github: Github
};

const CurrentFocus = () => {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative py-24 sm:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-indigo-500/5 to-transparent" />
      <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-purple-500/5 rounded-full blur-[100px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`transition-all duration-1000 transform ${visible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 px-3 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-xs font-mono mb-4">
              <Activity size={12} />
              <span>Current Focus</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              {portfolioData.currentFocus.title}
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {portfolioData.currentFocus.items.map((item, i) => {
              const Icon = focusIcons[item.icon] || Bot;
              return (
                <div key={item.name} className="glass rounded-2xl p-6 hover:border-indigo-500/20 transition-all duration-500 group hover:-translate-y-1"
                  style={{
                    opacity: visible ? 1 : 0,
                    transform: visible ? 'translateY(0)' : 'translateY(20px)',
                    transitionDelay: `${i * 80}ms`,
                    transitionProperty: 'all',
                    transitionDuration: '0.6s',
                    transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)'
                  }}>
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="text-indigo-400" size={20} />
                  </div>
                  <h3 className="text-white font-semibold text-sm mb-2">{item.name}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CurrentFocus;
